
import React, { useState, useMemo, useEffect } from 'react';
import type { Language, ShoppingCategory } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import { detectCategory } from '../utils/unitConversion';
import { SHOPPING_CATEGORIES, normalizeShoppingCategory } from '../utils/shoppingCategories';
import XIcon from './icons/XIcon';
import SmartUnitCapsule from './SmartUnitCapsule';

interface AddItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (name: string, category: string, isMeat: boolean, quantity: number, unit: string) => void;
    knownItems: { name: string; unit: string; isMeat: boolean; category?: string }[];
    language?: Language;
    categoryOverrides?: Record<string, string>;
    initialCategory?: string;
}

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose, onAdd, knownItems, language, categoryOverrides = {}, initialCategory }) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    
    const [name, setName] = useState('');
    const [category, setCategory] = useState<ShoppingCategory>('General');
    const [quantity, setQuantity] = useState(1);
    const [unit, setUnit] = useState('unit');
    const [isMeat, setIsMeat] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (initialCategory) {
                setCategory(normalizeShoppingCategory(initialCategory));
            } else {
                setCategory('General');
            }
            setName('');
            setQuantity(1);
            setUnit('unit');
            setIsMeat(false);
            setShowSuggestions(false);
        }
    }, [isOpen, initialCategory]);

    // Find if the currently typed name corresponds to an exact case-insensitive match in knownItems
    const matchedItem = useMemo(() => {
        const trimmed = name.toLowerCase().trim();
        if (!trimmed) return null;
        return knownItems.find(i => i.name.toLowerCase().trim() === trimmed);
    }, [name, knownItems]);

    // Auto-detect category when name changes, but only if category is 'General' and there is no complete autocomplete match
    useEffect(() => {
        if (!name.trim() || matchedItem) return;
        
        if (category === 'General') {
            const detected = normalizeShoppingCategory(categoryOverrides[name.toLowerCase().trim()] || detectCategory(name, isMeat));
            if (detected !== 'General') {
                setCategory(detected);
            }
        }
    }, [name, isMeat, category, categoryOverrides, matchedItem]);

    // Update form selections automatically if the user types or selects a matched item
    useEffect(() => {
        if (matchedItem) {
            const detected = normalizeShoppingCategory(matchedItem.category || categoryOverrides[matchedItem.name.toLowerCase().trim()] || detectCategory(matchedItem.name, matchedItem.isMeat));
            setCategory(detected);
            setIsMeat(matchedItem.isMeat);
            if (matchedItem.unit) {
                setUnit(matchedItem.unit);
            }
        }
    }, [matchedItem, categoryOverrides]);

    const suggestions = useMemo(() => {
        if (!name.trim()) return [];
        const lower = name.toLowerCase();
        return knownItems
            .filter(i => i.name.toLowerCase().includes(lower))
            .slice(0, 8);
    }, [name, knownItems]);

    if (!isOpen) return null;

    const handleSelectSuggestion = (item: { name: string; unit: string; isMeat: boolean; category?: string }) => {
        setName(item.name);
        setUnit(item.unit || 'unit');
        setIsMeat(item.isMeat);
        setShowSuggestions(false);
        
        const detected = normalizeShoppingCategory(item.category || categoryOverrides[item.name.toLowerCase().trim()] || detectCategory(item.name, item.isMeat));
        setCategory(detected);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        
        onAdd(name.trim(), category, isMeat, quantity, unit);
        // Reset state
        setName('');
        setQuantity(1);
        setCategory('General');
        setUnit('unit');
        setIsMeat(false);
        setShowSuggestions(false);
        onClose();
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        setShowSuggestions(true);
    };

    const handleInputFocus = () => {
        if (name.trim()) setShowSuggestions(true);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            {/* Click-outside layer for suggestions - z-index between modal and suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div 
                    className="fixed inset-0 z-[310] cursor-default" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowSuggestions(false);
                    }} 
                />
            )}

            <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors z-[320]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b bg-slate-50 dark:bg-slate-800/50 dark:border-slate-800 flex justify-between items-center">
                    <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('addItem')}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors p-2"><XIcon className="w-6 h-6" /></button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="relative group">
                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('nameLabel')}</label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={handleNameChange}
                            onFocus={handleInputFocus}
                            className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none font-bold text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 relative z-[320]"
                            placeholder={t('searchOrAdd')}
                            autoFocus
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 z-[330] overflow-hidden max-h-48 overflow-y-auto animate-fade-in">
                                {suggestions.map((s, idx) => (
                                    <button 
                                        key={idx} 
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectSuggestion(s);
                                        }}
                                        className="w-full text-start p-3 hover:bg-sky-50 dark:hover:bg-slate-700 text-sm font-bold text-slate-700 dark:text-slate-200 border-b border-slate-50 dark:border-slate-700 last:border-0 flex justify-between items-center transition-colors"
                                    >
                                        <span>{s.name}</span>
                                        {s.isMeat && <span className="text-[10px] text-rose-500 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 px-2 py-0.5 rounded-full">{t('meat')}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('quantity')}</label>
                        <div className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl flex items-center justify-center">
                            <SmartUnitCapsule 
                                value={quantity}
                                unit={unit}
                                onChange={(q, u) => {
                                    setQuantity(q);
                                    setUnit(u);
                                }}
                                language={language}
                                isMeat={isMeat}
                                zIndex={350}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('category')}</label>
                        <div className="relative">
                            <select 
                                value={category} 
                                onChange={e => setCategory(e.target.value as ShoppingCategory)} 
                                disabled={!!matchedItem}
                                className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none font-bold appearance-none cursor-pointer text-slate-700 dark:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {SHOPPING_CATEGORIES.map(c => (
                                    <option key={c} value={c} className="dark:bg-slate-800">{t(c.toLowerCase() as any)}</option>
                                ))}
                            </select>
                            <div className="absolute top-1/2 -translate-y-1/2 ltr:right-4 rtl:left-4 pointer-events-none text-slate-400">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                            </div>
                        </div>
                    </div>

                    <div className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${matchedItem ? 'opacity-50 cursor-not-allowed' : ''} ${isMeat ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`} onClick={() => !matchedItem && setIsMeat(!isMeat)}>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${isMeat ? 'bg-rose-500 border-rose-500' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'}`}>
                            {isMeat && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                        </div>
                        <span className={`text-xs font-bold uppercase tracking-wide ${isMeat ? 'text-rose-700 dark:text-rose-400' : 'text-slate-600 dark:text-slate-400'}`}>{t('markAsMeat')}</span>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-4 text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all">{t('cancel')}</button>
                        <button type="submit" disabled={!name} className="flex-1 py-4 bg-sky-600 text-white font-black rounded-xl uppercase tracking-widest text-xs shadow-lg shadow-sky-200 dark:shadow-sky-900/50 hover:bg-sky-700 transition-all disabled:opacity-50 active:scale-95">
                            {t('add')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddItemModal;